import React, { useRef } from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import type { RootState } from "@/store/store";
import {
  getBranchApi,
  getCategoryApi,
  getExpensesApi,
  getItemApi,
} from "@/modules/organizationServices";
import { setBranches } from "@/store/redux/organization/branchSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { setCategories } from "@/store/redux/organization/categorySlice";
import { removeFilter, setFilter } from "@/store/redux/filterSlice";
import { setExpenses } from "@/store/redux/organization/expenseSlice";

import type { ExpenseFilterProps, Filter } from "@/types/filter";
import {
  PaymentMethodsLabels,
  PaymentMethodsValues,
  ReimbursedLabels,
} from "@/enums/expenseFields";
import { DateList } from "@/enums/dateList";

import { Button } from "@/uiComponents/components/ui/button";
import { Card, CardContent } from "@/uiComponents/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/uiComponents/components/ui/popover";

import { Input } from "@/uiComponents/components/ui/input";
import { Checkbox } from "@/uiComponents/components/ui/checkbox";
import { Badge } from "@/uiComponents/components/ui/badge";
import { ChevronDown, FilterIcon, FilterX, Search, X } from "lucide-react";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { getFiltersFromUrl } from "@/utils/filterHelper";
import { setItems } from "@/store/redux/organization/itemSlice";

export function ExpenseFilter({ height }: ExpenseFilterProps) {
  const dateRef = useRef<HTMLDivElement>(null);
  const reimbursedRef = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Redux state
  const categories = useSelector(
    (state: RootState) => state.category.categories,
  );
  const branches = useSelector((state: RootState) => state.branch.branches);
  const items = useSelector((state: RootState) => state.item.items);
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );
  const orgId = userDetails?.organizationId;

  // Local state
  const [search, setSearch] = useState<string | null>();
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [isDateOpen, setIsDateOpen] = useState<boolean>(false);
  const [isReimbursedOpen, setIsReimbursedOpen] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCalenderOpen, setIsCalenderOpen] = useState(false);
  const [isDateCustom, setIsDateCustom] = useState(false);

  // Date calculations
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [selected, setSelected] = useState<{
    date: string;
    startDate: string;
    endDate: string;
    categoryId: number[];
    branchId: number[];
    itemsId: number[];
    paymentMethod: string[];
    search: string;
    reimbursed: string;
  }>({
    date: "",
    startDate: "",
    endDate: "",
    categoryId: [],
    branchId: [],
    itemsId: [],
    paymentMethod: [],
    search: "",
    reimbursed: "",
  });

  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({
    payment: false,
    category: false,
    branch: false,
    item: false,
    date: false,
    reimbursed: false,
  });

  const dateOptions = [
    { value: DateList.LAST_WEEK, label: DateList.LAST_WEEK },
    { value: DateList.LAST_THREE_MONTHS, label: DateList.LAST_THREE_MONTHS },
    { value: DateList.LAST_SIX_MONTHS, label: DateList.LAST_SIX_MONTHS },
    { value: DateList.LAST_YEAR, label: DateList.LAST_YEAR },
    { value: DateList.CUSTOM, label: DateList.CUSTOM },
  ];

  const paymentOptions = [
    { value: PaymentMethodsValues.CASH, label: PaymentMethodsLabels.CASH },
    { value: PaymentMethodsValues.CHEQUE, label: PaymentMethodsLabels.CHEQUE },
    { value: PaymentMethodsValues.QR, label: PaymentMethodsLabels.ONLINE },
  ];

  const reimbursedOptions = [
    { value: "true", label: ReimbursedLabels.DONE },
    {
      value: "false",
      label: ReimbursedLabels.PENDING,
    },
  ];

  // Update URL with filters
  const updateFiltersInUrl = (filters: Filter) => {
    const params = new URLSearchParams();

    // Clear existing params first
    setSearchParams({});

    if (filters.date) params.set("date", filters.date);
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate) params.set("endDate", filters.endDate);
    if (filters.itemsId?.length)
      params.set("itemsId", filters.itemsId.join(","));
    if (filters.branchId?.length)
      params.set("branchId", filters.branchId.join(","));
    if (filters.categoryId?.length)
      params.set("categoryId", filters.categoryId.join(","));
    if (filters.paymentMethod?.length)
      params.set("paymentMethod", filters.paymentMethod.join(","));
    if (filters.search) params.set("search", filters.search);
    if (filters.reimbursed) params.set("reimbursed", filters.reimbursed);
    setSearchParams(params);
  };

  //for date div
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dateRef.current && !dateRef.current.contains(event.target as Node)) {
        setIsDateOpen(false);
        setOpenDropdowns((prev) => ({ ...prev, date: false }));
      }
      if (
        reimbursedRef.current &&
        !reimbursedRef.current.contains(event.target as Node)
      ) {
        setIsReimbursedOpen(false);
        setOpenDropdowns((prev) => ({ ...prev, reimbursed: false }));
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Date range calculation based on selected date option

  useEffect(() => {
    if (!selected.date) return;

    const today = new Date();
    let startDate = new Date();

    switch (selected.date) {
      case DateList.LAST_WEEK:
        startDate.setDate(today.getDate() - 7);
        break;
      case DateList.LAST_MONTH:
        startDate.setMonth(today.getMonth() - 1);
        break;
      case DateList.LAST_THREE_MONTHS:
        startDate.setMonth(today.getMonth() - 3);
        break;
      case DateList.LAST_SIX_MONTHS:
        startDate.setMonth(today.getMonth() - 6);
        break;
      case DateList.LAST_YEAR:
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      case DateList.CUSTOM:
        setIsDateCustom(true);
        setIsCalenderOpen(true);
        return; // Exit early for custom dates
      default:
        return;
    }

    // Fix for cases like Jan 31 -> Feb 28/29
    if (startDate > today) {
      startDate = new Date(today.getFullYear(), today.getMonth(), 0);
    }

    setStartDate(startDate.toISOString().split("T")[0]);
    setEndDate(today.toISOString().split("T")[0]);
  }, [selected.date]);

  const toggleOption = <T extends string | number>(
    type: keyof typeof selected,
    value: T,
  ) => {
    setSelected((prev) => {
      const current = prev[type] as T[];
      return {
        ...prev,
        [type]: current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  const getBranches = async () => {
    try {
      if (!orgId) {
        return;
      }
      const response = await getBranchApi(orgId);
      if (response.branches != undefined) {
        const branches = Array.isArray(response.branches)
          ? response.branches
          : [response.branches];
        dispatch(setBranches(branches));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const getCategories = async () => {
    try {
      const response = await getCategoryApi();
      if (response.categories != undefined) {
        const categories = Array.isArray(response.categories)
          ? response.categories
          : [response.categories];
        dispatch(setCategories(categories));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };
  const getItems = async () => {
    try {
      const response = await getItemApi();
      if (response.items != undefined) {
        const items = Array.isArray(response.items)
          ? response.items
          : [response.items];
        dispatch(setItems(items));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const activeFilters = getFiltersFromUrl(searchParams);

  // Calculate active filter count based on URL params
  const activeFilterCount = [
    activeFilters.date,
    ...activeFilters.categoryId,
    ...activeFilters.branchId,
    ...activeFilters.itemsId,
    ...activeFilters.paymentMethod,
    activeFilters.reimbursed,
    activeFilters.search,
  ].filter(Boolean).length;

  useEffect(() => {
    const filtersFromUrl = getFiltersFromUrl(searchParams);

    setSelected(filtersFromUrl);
    setStartDate(startDate);
    setEndDate(endDate);
    setSearch(filtersFromUrl.search);
    dispatch(setFilter(filtersFromUrl));
  }, []);

  // Load data on component mount
  useEffect(() => {
    getCategories();
    getBranches();
    getItems();
  }, []);

  // Filter actions
  const filter: Filter = {
    date: selected.date,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    categoryId: selected.categoryId,
    branchId: selected.branchId,
    itemsId: selected.itemsId,
    paymentMethod: selected.paymentMethod,
    search: selected.search || "",
    reimbursed: selected.reimbursed || "",
  };

  const handleApply = async () => {
    dispatch(setFilter(filter));
    updateFiltersInUrl(selected);
    const expenses = await getExpensesApi(1, filter);
    dispatch(setExpenses(expenses));
  };

  const handleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
    if (isFilterOpen) {
      handleReset();
    }
  };
  const handleReset = async () => {
    const resetSelected = {
      date: "",
      startDate: "",
      endDate: "",
      categoryId: [],
      branchId: [],
      itemsId: [],
      paymentMethod: [],
      search: "",
      reimbursed: "",
    };

    const updatedFilter: Filter = {
      ...resetSelected,
    };
    const expenses = await getExpensesApi(1, updatedFilter);
    setSelected(resetSelected);
    setSearchParams({});
    setSearch(null);
    setIsDateCustom(false);

    dispatch(removeFilter());
    dispatch(setExpenses(expenses));
  };

  return (
    <>
      <div className="relative flex ">
        <Search
          className={`absolute left-3 top-1/2 transform -translate-y-1/2  h-${height} w-5`}
        />
        <Input
          className={`pl-10 h-${height} cursor-text bg-white `}
          type="text"
          placeholder="Search Description"
          value={search ?? ""}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelected((prev) => ({ ...prev, search: e.target.value }));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleApply();
            }
          }}
        />
        <Button
          className={`${activeFilterCount > 0 ? " bg-[#191F29]  hover:bg-[#2A313D] active:bg-[#001533] " : "bg-white text-black hover:bg-white hover:text-black"} cursor-pointer ml-2 h-12 rounded-lg border-gray-200 border-2`}
          onClick={() => {
            handleFilter();
            setIsFilterExpanded(!isFilterExpanded);
          }}
        >
          {isFilterOpen ? <FilterX /> : <FilterIcon />}
        </Button>
      </div>
      <div
        className={`
    overflow-visible transition-all duration-300 ease-in-out
    transform
    ${isFilterExpanded || isFilterOpen ? "max-h-[500px] opacity-100 scale-100 z-50" : "max-h-0 opacity-0 scale-95"}
    space-y-2
  `}
      >
        <Card
          className={`w-full mt-2 ${isFilterExpanded || isFilterOpen ? "" : "shadow-sm opacity-0 pointer-events-none"} `}
        >
          <CardContent>
            <div className="grid  grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {!isDateCustom && (
                <div className="space-y-1">
                  <label className="text-sm  font-medium">Date Range</label>

                  <div className="relative cursor-pointer " ref={dateRef}>
                    <div
                      onClick={() => {
                        setIsDateOpen(!isDateOpen);
                        setOpenDropdowns((prev) => ({
                          ...prev,
                          date: !prev.date,
                        }));
                      }}
                      className={`w-full  flex border p-1 rounded-md justify-between ${
                        openDropdowns.date
                          ? "border-blue-500 ring-2 ring-blue-200"
                          : ""
                      }`}
                    >
                      <span className="ml-4">{selected.date || "Any"}</span>
                      <ChevronDown
                        size={16}
                        className={`mr-2 mt-1 transition-transform duration-200 ${
                          openDropdowns.date ? "rotate-180" : ""
                        }`}
                      />
                    </div>

                    {/* Dropdown */}
                    {isDateOpen && (
                      <ul className="  absolute left-0 top-full mt-1 w-full  border rounded shadow bg-white ">
                        {dateOptions.map((option) => (
                          <li
                            key={option.value}
                            onClick={() => {
                              setIsDateOpen(false);
                              setSelected((prev) => ({
                                ...prev,
                                date: option.value,
                              }));
                            }}
                            className="w-full cursor-pointer px-4 py-2 hover:bg-gray-100"
                          >
                            {option.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {isDateCustom && (
                <div className=" space-y-1 relative flex flex-col">
                  <label className="text-sm  font-medium">Date Range</label>
                  <DatePicker
                    className={
                      "w-full bg-white cursor-pointer text-black hover:bg-white hover:text-black border rounded-md mt-1 h-9 pl-4 justify-between"
                    }
                    selectsRange
                    startDate={startDate ? new Date(startDate) : null}
                    endDate={endDate ? new Date(endDate) : null}
                    onChange={(dates) => {
                      const [start, end] = dates;

                      const formattedStartDate = start
                        ? start.toLocaleDateString("en-CA")
                        : "";
                      const formattedEndDate = end
                        ? end.toLocaleDateString("en-CA")
                        : "";
                      setStartDate(formattedStartDate);
                      setEndDate(formattedEndDate);
                      setSelected((prev) => ({
                        ...prev,
                        startDate: formattedStartDate,
                        endDate: formattedEndDate,
                      }));
                      if (start && end) {
                        setIsCalenderOpen(false);
                      }
                    }}
                    open={isCalenderOpen}
                    onClickOutside={() => setIsCalenderOpen(false)} // Close when clicking outside
                    onInputClick={() => setIsCalenderOpen(true)}
                    autoFocus
                    maxDate={new Date()}
                  />
                  <Button
                    className="w-2 h-5 cursor-pointer bg-gray-100 text-black hover:text-destructive hover:bg-gray-100 absolute right-2 top-8"
                    onClick={() => {
                      setIsDateCustom(false);
                      setSelected((prev) => ({
                        ...prev,
                        date: "",
                      }));
                    }}
                  >
                    <X />
                  </Button>
                </div>
              )}

              {/* Payment Method */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Payment Method</label>
                <Popover
                  open={openDropdowns.payment}
                  onOpenChange={(open) =>
                    setOpenDropdowns((prev) => ({ ...prev, payment: open }))
                  }
                >
                  <PopoverTrigger asChild>
                    <Button
                      className={`w-full bg-white cursor-pointer text-black hover:bg-white hover:text-black border justify-between ${openDropdowns.payment ? "border-blue-500 ring-2 ring-blue-200" : ""}`}
                    >
                      {selected.paymentMethod.length > 0
                        ? selected.paymentMethod.length === 1
                          ? paymentOptions.find((p) =>
                              selected.paymentMethod.includes(p.value),
                            )?.label
                          : `${selected.paymentMethod.length} selected`
                        : "Any"}
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${openDropdowns.payment ? "rotate-180" : ""}`}
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div className="p-1 space-y-1">
                      {paymentOptions.map((option) => (
                        <label
                          key={option.value}
                          htmlFor={`payment-${option.value}`}
                          className="flex items-center space-x-2 h-9 cursor-pointer px-2 rounded hover:bg-gray-100"
                        >
                          <Checkbox
                            id={`payment-${option.value}`}
                            className="cursor-pointer"
                            checked={selected.paymentMethod.includes(
                              option.value,
                            )}
                            onCheckedChange={() =>
                              toggleOption("paymentMethod", option.value)
                            }
                          />
                          <span className="leading-none">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Category */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Category</label>
                <Popover
                  open={openDropdowns.category}
                  onOpenChange={(open) =>
                    setOpenDropdowns((prev) => ({ ...prev, category: open }))
                  }
                >
                  <PopoverTrigger asChild>
                    <Button
                      className={`w-full cursor-pointer bg-white text-black hover:bg-white hover:text-black border justify-between ${openDropdowns.category ? "border-blue-500 ring-2 ring-blue-200" : ""}`}
                    >
                      {selected.categoryId.length > 0
                        ? selected.categoryId.length === 1
                          ? categories.find((c) =>
                              selected.categoryId.includes(c.id),
                            )?.name
                          : `${selected.categoryId.length} selected`
                        : "Any"}
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${openDropdowns.category ? "rotate-180" : ""}`}
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div className="p-1 space-y-1">
                      {categories.map((option) => (
                        <label
                          key={option.id}
                          htmlFor={`category-${option.id}`}
                          className="flex items-center space-x-2 h-9 cursor-pointer px-2 rounded hover:bg-gray-100"
                        >
                          <Checkbox
                            className="cursor-pointer"
                            id={`category-${option.id}`}
                            checked={selected.categoryId.includes(option.id)}
                            onCheckedChange={() =>
                              toggleOption("categoryId", option.id)
                            }
                          />
                          <span className="leading-none">{option.name}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Branch */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Branch</label>
                <Popover
                  open={openDropdowns.branch}
                  onOpenChange={(open) =>
                    setOpenDropdowns((prev) => ({ ...prev, branch: open }))
                  }
                >
                  <PopoverTrigger asChild>
                    <Button
                      className={`w-full cursor-pointer bg-white text-black hover:bg-white hover:text-black border justify-between ${openDropdowns.branch ? "border-blue-500 ring-2 ring-blue-200" : ""}`}
                    >
                      {selected.branchId.length > 0
                        ? selected.branchId.length === 1
                          ? branches.find((b) =>
                              selected.branchId.includes(b.id),
                            )?.branchName
                          : `${selected.branchId.length} selected`
                        : "Any"}
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${openDropdowns.branch ? "rotate-180" : ""}`}
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div className="p-1 space-y-1">
                      {branches.map((option) => (
                        <label
                          key={option.id}
                          htmlFor={`branch-${option.id}`}
                          className="flex items-center space-x-2 h-9 cursor-pointer px-2 rounded hover:bg-gray-100"
                        >
                          <Checkbox
                            className="cursor-pointer"
                            id={`branch-${option.id}`}
                            checked={selected.branchId.includes(option.id)}
                            onCheckedChange={() =>
                              toggleOption("branchId", option.id)
                            }
                          />
                          <span className="leading-none">
                            {option.branchName}
                          </span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Items */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Items</label>
                <Popover
                  open={openDropdowns.item}
                  onOpenChange={(open) =>
                    setOpenDropdowns((prev) => ({ ...prev, item: open }))
                  }
                >
                  <PopoverTrigger asChild>
                    <Button
                      className={`w-full cursor-pointer bg-white text-black hover:bg-white hover:text-black border justify-between ${openDropdowns.item ? "border-blue-500 ring-2 ring-blue-200" : ""}`}
                    >
                      {selected.itemsId.length > 0
                        ? selected.itemsId.length === 1
                          ? items.find((t) => selected.itemsId.includes(t.id))
                              ?.name
                          : `${selected.itemsId.length} selected`
                        : "Any"}
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${openDropdowns.item ? "rotate-180" : ""}`}
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <div className="p-1 space-y-1">
                      {items.map((option) => (
                        <label
                          key={option.id}
                          htmlFor={`item-${option.id}`}
                          className="flex items-center space-x-2 h-9 cursor-pointer px-2 rounded hover:bg-gray-100"
                        >
                          <Checkbox
                            className="cursor-pointer"
                            id={`item-${option.id}`}
                            checked={selected.itemsId.includes(option.id)}
                            onCheckedChange={() =>
                              toggleOption("itemsId", option.id)
                            }
                          />
                          <span className="leading-none">{option.name}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-sm  font-medium">Reimbursed</label>

                <div className="relative cursor-pointer " ref={reimbursedRef}>
                  <div
                    onClick={() => {
                      setIsReimbursedOpen(!isReimbursedOpen);
                      setOpenDropdowns((prev) => ({
                        ...prev,
                        reimbursed: !prev.reimbursed,
                      }));
                    }}
                    className={`w-full  flex border p-1 rounded-md justify-between ${
                      openDropdowns.reimbursed
                        ? "border-blue-500 ring-2 ring-blue-200"
                        : ""
                    }`}
                  >
                    {selected.reimbursed.length > 0
                      ? reimbursedOptions.find((r) =>
                          selected.reimbursed.includes(r.value),
                        )?.label
                      : "Any"}
                    <ChevronDown
                      size={16}
                      className={`mr-2 mt-1 transition-transform duration-200 ${
                        openDropdowns.reimbursed ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {/* Dropdown */}
                  {isReimbursedOpen && (
                    <ul className="  absolute left-0 top-full mt-1 w-full  border rounded shadow bg-white ">
                      {reimbursedOptions.map((option) => (
                        <li
                          key={option.value}
                          onClick={() => {
                            setIsReimbursedOpen(false);
                            setSelected((prev) => ({
                              ...prev,
                              reimbursed: option.value,
                            }));
                          }}
                          className="w-full cursor-pointer px-4 py-2 hover:bg-gray-100"
                        >
                          {option.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="mt-6 ml-5">
                <Button
                  onClick={handleApply}
                  className="bg-[#191F29] cursor-pointer  hover:bg-[#2A313D] active:bg-[#001533] text-white "
                >
                  Apply Filters
                </Button>
              </div>
            </div>

            {/* Active filters display */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {selected.date && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {dateOptions.find((d) => d.value === selected.date)?.label}
                    <button
                      onClick={() =>
                        setSelected((prev) => ({ ...prev, date: "" }))
                      }
                      className="ml-1 cursor-pointer hover:text-destructive"
                    >
                      <X size={16} />
                    </button>
                  </Badge>
                )}

                {selected.categoryId.map((id) => {
                  const category = categories.find((c) => c.id === id);
                  return category ? (
                    <Badge
                      key={`cat-${id}`}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {category.name}
                      <button
                        onClick={() => toggleOption("categoryId", id)}
                        className="ml-1 cursor-pointer hover:text-destructive"
                      >
                        <X size={16} />
                      </button>
                    </Badge>
                  ) : null;
                })}

                {selected.branchId.map((id) => {
                  const branch = branches.find((b) => b.id === id);
                  return branch ? (
                    <Badge
                      key={`branch-${id}`}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {branch.branchName}
                      <button
                        onClick={() => toggleOption("branchId", id)}
                        className="ml-1 cursor-pointer hover:text-destructive"
                      >
                        <X size={16} />
                      </button>
                    </Badge>
                  ) : null;
                })}

                {selected.itemsId.map((id) => {
                  const item = items.find((t) => t.id === id);
                  return item ? (
                    <Badge
                      key={`item-${id}`}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {item.name}
                      <button
                        onClick={() => toggleOption("itemsId", id)}
                        className="ml-1 cursor-pointer hover:text-destructive"
                      >
                        <X size={16} />
                      </button>
                    </Badge>
                  ) : null;
                })}

                {selected.paymentMethod.map((method) => {
                  const payment = paymentOptions.find(
                    (p) => p.value === method,
                  );
                  return payment ? (
                    <Badge
                      key={`payment-${method}`}
                      variant="secondary"
                      className="flex items-center gap-1 "
                    >
                      {payment.label}
                      <button
                        onClick={() => toggleOption("paymentMethod", method)}
                        className="ml-1 cursor-pointer hover:text-destructive"
                      >
                        <X size={16} />
                      </button>
                    </Badge>
                  ) : null;
                })}
                {selected.reimbursed && (
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {
                      reimbursedOptions.find(
                        (d) => d.value === selected.reimbursed,
                      )?.label
                    }
                    <button
                      onClick={() =>
                        setSelected((prev) => ({ ...prev, reimbursed: "" }))
                      }
                      className="ml-1 cursor-pointer hover:text-destructive"
                    >
                      <X size={16} />
                    </button>
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
